import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectActiveLayer, getActiveStep, setActiveStep, removeActiveLayer } from '../../../../../../redux/toolsSlice';
import { updateStep, deleteStep, allSteps, getMainConfig, updateMainConfig, upsertMainConfigValue } from '../../../../../../redux/stepsSlice';
import { getMaterial, updateMaterial } from '../../../../../../redux/materialSlice';
import { getValue } from '../../../../utils/tools';
import ElementControls from './ElementControls';

const chev = (
  <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M6.53 13.83 10.35 10 6.53 6.18 7.7 5l5 5-5 5-1.17-1.17Z" fill="#9AA6AE" /></svg>
);

const Toggle = ({ on, onClick }) => (
  <button type="button" className={`un-pe-toggle ${on ? 'on' : 'off'}`} onClick={onClick} aria-pressed={on}><i /></button>
);

const STATUS_OPTIONS = ['Not started', 'In progress', 'Stuck', 'Completed', 'Skipped'];

const FONT_OPTIONS = [
  { key: 'Default', value: 'Open Sans' },
  { key: 'Serif', value: 'Georgia, serif' },
  { key: 'Mono', value: 'ui-monospace, Menlo, monospace' },
];

// ---- Path (top-level) inspector -------------------------------------------
const PathInspector = () => {
  const dispatch = useDispatch();
  const material = useSelector((state) => getMaterial(state));
  const mainConfig = useSelector((state) => getMainConfig(state));

  const [themeOpen, setThemeOpen] = useState(false);

  const config = Array.isArray(mainConfig) && mainConfig.length > 0 ? mainConfig : material?.config;
  const defaultColors = { background: '#F6F8F9', text: '#363841', config };
  const haBGColor = getValue(defaultColors, 'ha', 'haBGColor') || '#2D3E50';
  const haTextColor = getValue(defaultColors, 'ha', 'haTextColor') || '#FFFFFF';
  const haFont = getValue(defaultColors, 'ha', 'haFont') || 'Open Sans';
  const caHeaderColor = getValue(defaultColors, 'ca', 'caHeaderColor') || '#F3F3F3';
  const caTextColor = getValue(defaultColors, 'ca', 'caTextColor') || '#363841';
  const baBGColor = getValue(defaultColors, 'ba', 'baHeaderColor') || '#F6F8F9';
  const haShowSignIn = getValue(defaultColors, 'ha', 'haShowSignIn');
  const headerLogo = getValue(defaultColors, 'phc', 'phcHeaderLogo') || '';
  const coverImage = getValue(defaultColors, 'phc', 'phcCoverImage') || '';

  const setHa = (titleId, value) => dispatch(updateMainConfig({ sectionId: 'ha', titleId, value }));
  const setCa = (titleId, value) => dispatch(updateMainConfig({ sectionId: 'ca', titleId, value }));
  const setBa = (titleId, value) => dispatch(updateMainConfig({ sectionId: 'ba', titleId, value }));
  const setPhc = (titleId, value) => dispatch(updateMainConfig({ sectionId: 'phc', titleId, value }));

  return (
    <div className="un-pe-insp">
      <div className="un-pe-isel">
        <span className="un-pe-isel__ico" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 21 21" fill="none"><path d="M18.4 8.75c0 6.1-7.9 11.4-7.9 11.4S2.6 14.9 2.6 8.75a7.9 7.9 0 1 1 15.8 0Z" stroke="#028496" strokeWidth="1.6" /><circle cx="10.5" cy="8.75" r="2.6" stroke="#028496" strokeWidth="1.6" /></svg>
        </span>
        Path settings
        <span className="un-pe-isel__cur">{material?.name || 'Path'}</span>
      </div>

      <p className="un-pe-lab">Path name</p>
      <input
        className="un-pe-field"
        value={material?.name || ''}
        placeholder="Untitled path"
        onChange={(e) => dispatch(updateMaterial({ name: e.target.value }))}
      />

      <p className="un-pe-lab">Brand logo <span className="un-pe-opt">· image URL</span></p>
      <input
        className="un-pe-field"
        value={headerLogo}
        placeholder="https://…/logo.png"
        onChange={(e) => setPhc('phcHeaderLogo', e.target.value)}
      />

      <p className="un-pe-lab">Cover image <span className="un-pe-opt">· optional</span></p>
      <input
        className="un-pe-field"
        value={coverImage}
        placeholder="https://…/cover.jpg"
        onChange={(e) => setPhc('phcCoverImage', e.target.value)}
      />

      <div className={`un-pe-accord${themeOpen ? ' is-open' : ''}`} style={{ marginTop: 14 }}>
        <button type="button" className="un-pe-accord__h" onClick={() => setThemeOpen((v) => !v)}>
          Theme — colors &amp; fonts
          <svg width="16" height="16" viewBox="0 0 20 20" fill="none"><path d="M6.53 13.83 10.35 10 6.53 6.18 7.7 5l5 5-5 5-1.17-1.17Z" fill="#9AA6AE" /></svg>
        </button>
        {themeOpen && (
          <div className="un-pe-accord__b">
            <p className="un-pe-lab">Header background</p>
            <label className="un-pe-colorrow">
              <input type="color" value={haBGColor} onChange={(e) => setHa('haBGColor', e.target.value)} />
              Header<span className="hex">{haBGColor}</span>
            </label>
            <p className="un-pe-lab">Header text</p>
            <label className="un-pe-colorrow">
              <input type="color" value={haTextColor} onChange={(e) => setHa('haTextColor', e.target.value)} />
              Text<span className="hex">{haTextColor}</span>
            </label>
            <p className="un-pe-lab">Step header bar</p>
            <label className="un-pe-colorrow">
              <input type="color" value={caHeaderColor} onChange={(e) => setCa('caHeaderColor', e.target.value)} />
              Bar<span className="hex">{caHeaderColor}</span>
            </label>
            <p className="un-pe-lab">Body text</p>
            <label className="un-pe-colorrow">
              <input type="color" value={caTextColor} onChange={(e) => setCa('caTextColor', e.target.value)} />
              Text<span className="hex">{caTextColor}</span>
            </label>
            <p className="un-pe-lab">Page background</p>
            <label className="un-pe-colorrow">
              <input type="color" value={baBGColor} onChange={(e) => setBa('baHeaderColor', e.target.value)} />
              Page<span className="hex">{baBGColor}</span>
            </label>
            <p className="un-pe-lab">Font</p>
            <div className="un-pe-seg2">
              {FONT_OPTIONS.map((f) => (
                <button
                  key={f.key}
                  type="button"
                  className={`un-pe-seg2__s${haFont === f.value ? ' on' : ''}`}
                  style={{ fontFamily: f.value }}
                  onClick={() => setHa('haFont', f.value)}
                >{f.key}</button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="un-pe-torow" style={{ marginTop: 14 }}>
        <span className="un-pe-torow__l">Show &ldquo;Sign in&rdquo; button</span>
        <Toggle
          on={haShowSignIn !== false}
          onClick={() => dispatch(upsertMainConfigValue({ sectionId: 'ha', sectionTitle: 'Header Appearance', titleId: 'haShowSignIn', title: 'Show sign in button', type: 'toggle', value: haShowSignIn === false }))}
        />
      </div>

      <div className="un-pe-rulesrow" style={{ marginTop: 14 }}>
        <svg width="15" height="15" viewBox="0 0 21 19" fill="none"><circle cx="10.5" cy="4" r="3" stroke="#5A6B7E" strokeWidth="1.5" /><path d="M4 16v-.5A4.5 4.5 0 0 1 8.5 11h4a4.5 4.5 0 0 1 4.5 4.5v.5" stroke="#5A6B7E" strokeWidth="1.5" /></svg>
        Audience &amp; assignment
        <span className="un-pe-rulesrow__meta">{material?.audience_count != null ? `${material.audience_count} people` : 'Manage'}</span>
        <span className="chev">{chev}</span>
      </div>

      <p className="un-pe-subtle">Hero headline, sub-header, and description are edited directly on the page — click them on the canvas.</p>
    </div>
  );
};

// ---- Step inspector --------------------------------------------------------
const StepInspector = () => {
  const dispatch = useDispatch();
  const activeStep = useSelector((state) => getActiveStep(state));
  const steps = useSelector((state) => allSteps(state));

  const setStep = (patch) => activeStep?.id && dispatch(updateStep({ ...activeStep, ...patch }));

  const hasRule = activeStep?.display_rule && Array.isArray(activeStep.display_rule.conditions)
    && activeStep.display_rule.conditions.some((c) => c && c.attribute);
  const completion = activeStep?.completion_mode || 'manual';
  const stepNumber = Math.max(0, steps.findIndex((s) => s.id === activeStep?.id)) + 1;

  const onDelete = () => {
    if (!activeStep?.id) return;
    if (!window.confirm('Delete this step? This cannot be undone.')) return;
    const idx = steps.findIndex((s) => s.id === activeStep.id);
    const next = steps[idx - 1] || steps[idx + 1] || null;
    dispatch(deleteStep(activeStep.id));
    dispatch(removeActiveLayer());
    if (next) dispatch(setActiveStep(next));
  };

  return (
    <div className="un-pe-insp">
      <div className="un-pe-isel">
        <span className="un-pe-isel__ico" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><rect x="3" y="3" width="14" height="14" rx="4" stroke="#028496" strokeWidth="1.6" /><path d="M7 10h6" stroke="#028496" strokeWidth="1.6" strokeLinecap="round" /></svg>
        </span>
        Editing step
        <span className="un-pe-isel__cur">Step {stepNumber}</span>
      </div>

      <p className="un-pe-lab">Step title</p>
      <input className="un-pe-field" value={activeStep?.title || ''} placeholder="Untitled step" onChange={(e) => setStep({ title: e.target.value })} />

      <p className="un-pe-lab">Category label <span className="un-pe-opt">· eyebrow</span></p>
      <input className="un-pe-field" value={activeStep?.name || ''} placeholder="e.g. Quick product demo video" onChange={(e) => setStep({ name: e.target.value })} />

      <p className="un-pe-lab">Default status</p>
      <select className="un-pe-field" value={activeStep?.default_status || 'Not started'} onChange={(e) => setStep({ default_status: e.target.value })}>
        {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
      </select>

      <p className="un-pe-lab">Due date <span className="un-pe-opt">· optional</span></p>
      <input
        type="date"
        className="un-pe-field un-pe-datein"
        value={activeStep?.dueDate ? new Date(activeStep.dueDate).toISOString().slice(0, 10) : ''}
        onChange={(e) => setStep({ dueDate: e.target.value ? new Date(e.target.value).toISOString() : '' })}
      />

      <p className="un-pe-lab">Completion</p>
      <div className="un-pe-seg2">
        <button type="button" className={`un-pe-seg2__s${completion === 'manual' ? ' on' : ''}`} onClick={() => setStep({ completion_mode: 'manual' })}>Learner marks done</button>
        <button type="button" className={`un-pe-seg2__s${completion === 'all_elements' ? ' on' : ''}`} onClick={() => setStep({ completion_mode: 'all_elements' })}>All elements</button>
      </div>

      <div className="un-pe-rulesrow" style={{ marginTop: 14 }}>
        <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><path d="M3 4h14M6 10h8M9 16h2" stroke="#2B7A93" strokeWidth="2" strokeLinecap="round" /></svg>
        Display rules
        <span className="un-pe-rulesrow__meta">{hasRule ? 'Custom' : 'Everyone'}</span>
        <span className="chev">{chev}</span>
      </div>

      <div className="un-pe-torow" style={{ marginTop: 14 }}>
        <span className="un-pe-torow__l">
          <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><path d="M17 9.5a6.5 6.5 0 0 1-9.3 5.9L3 16.5l1.1-4.7A6.5 6.5 0 1 1 17 9.5Z" stroke="#5A6B7E" strokeWidth="1.5" /></svg>
          Learner comments
        </span>
        <Toggle on={Boolean(activeStep?.allow_comments)} onClick={() => setStep({ allow_comments: !activeStep?.allow_comments })} />
      </div>

      <div className="un-pe-torow">
        <span className="un-pe-torow__l">
          <svg width="15" height="15" viewBox="0 0 20 20" fill="none"><path d="M10 3v5l3 2" stroke="#5A6B7E" strokeWidth="1.5" strokeLinecap="round" /><circle cx="10" cy="10" r="7.2" stroke="#5A6B7E" strokeWidth="1.5" /></svg>
          Show self-status control
        </span>
        <Toggle on={activeStep?.show_status_control !== false} onClick={() => setStep({ show_status_control: activeStep?.show_status_control === false })} />
      </div>

      <button type="button" className="un-pe-danger" onClick={onDelete}>
        <svg width="14" height="14" viewBox="0 0 24 24" fill="#C0392B"><path d="M10 2 9 3H5a1 1 0 0 0 0 2h14a1 1 0 0 0 0-2h-4l-1-1h-4ZM5 7v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7H5Z" /></svg>
        Delete step
      </button>

      <p className="un-pe-subtle">Hero headline, sub-header, and description are edited directly on the page — click them on the canvas.</p>
    </div>
  );
};

// Right contextual inspector. Element selected → per-element controls;
// path scope (hero clicked) → Path inspector; otherwise → Step inspector.
const InspectorV2 = ({ scope = 'step' }) => {
  const activeLayer = useSelector((state) => selectActiveLayer(state));
  if (activeLayer?.id) return <ElementControls />;
  if (scope === 'path') return <PathInspector />;
  return <StepInspector />;
};

export default InspectorV2;
